import React, { useCallback, useMemo } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import set from 'lodash/set';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import BannerComponent from 'src/presentation/components/pages/routing/BannerComponent';
import { Button } from 'src/presentation/components/agent_desktop_components';
import Checkbox from 'src/presentation/components/forms/Checkbox';
import { ControlDescription, ControlLabel } from 'src/presentation/components/agent_desktop_components';
import conversationWorkflowResource from 'src/actions/conversationWorkflow/conversationWorkflow';
import Dropdown from 'src/presentation/components/forms/Dropdown';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import { FieldWrapper } from '@ad-scripts/presentation/components/common/input_field';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { useForm, FormEditorProps } from 'src/presentation/components/forms/Form';
import {
  getConversationWorkflowData,
  getMessageData,
  getTaskData,
  getVoiceData,
} from 'src/presentation/components/pages/routing/hooks/useRoutes';
import { H3 } from '@ad-scripts/presentation/components/common/headers';
import InputField from 'src/presentation/components/forms/InputField';
import InsetContainer from '@ad-scripts/presentation/components/common/containers/inset_container';
import messagingResource from 'src/actions/messaging/MessagingResource';
import ReversedValueCheckbox from 'src/presentation/components/pages/shared/ReversedValueCheckbox';
import { Row, Section, StyledSection } from 'src/presentation/components/forms/Layout';
import { StyledRadioGroup } from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/shared/elements';
import Switch from 'src/presentation/components/forms/Switch';
import taskRoutingResource from 'src/actions/taskRouting/taskRouting';
import TimeInputField, { TimeUnits } from 'src/presentation/components/pages/shared/MinutesInputField';
import useAgents from 'src/presentation/components/pages/rules/hooks/useAgents';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';
import useOnLeavePage from 'src/presentation/components/pages/shared/hooks/useOnLeavePage';
import voiceConfigResource from 'src/actions/voice/voiceConfigResource';
import { useResourceAll } from 'src/presentation/hooks/useResource';
import agentsResource from 'src/actions/agent/agentsResource';
import { ControlWrapper } from '@ad-scripts/presentation/components/common/forms/control_container';

const SameAgentInputDescriptionEmail = (
  <>
    After sending a reply, the amount of time an email or voicemail contact remains active and is routed to the same
    Agent.{` `}
    <ExternalLink
      data-aid="routing-form-settings-help"
      label="See recommended settings"
      to="https://help.gladly.com/docs/configure-mail-routing-settings"
    />
    .
  </>
);

const SameAgentInputDescriptionMessage = () => (
  <>
    After sending a reply, the amount of time a messaging contact remains active and is routed to the same Agent{` `}
    <StyledStrong>only if they’re available for messaging.</StyledStrong>
    {` `}
    <ExternalLink
      data-aid="routing-form-settings-help"
      label="See recommended settings"
      to="https://help.gladly.com/docs/configure-messaging-routing-settings"
    />
    .
  </>
);

const renderFocusOffMessage = () => (
  <>
    <FocusStatus data-aid="focus-mode-description">Focus is turned off</FocusStatus>
    Activate Focus to give agents the ability to pause the routing engine without needing to go unavailable on any
    channel. For best practices for incorporating the Focus button into your workflow, {` `}
    <ExternalLink
      data-aid="enable-focus-help-link"
      label="check out the help docs"
      to="https://help.gladly.com/docs/configure-focus-settings"
    />
    .
  </>
);

const renderFocusOnMessage = () => (
  <>
    <FocusStatus data-aid="focus-mode-description">Focus is turned on</FocusStatus>
    Deactivating Focus removes the ability to pause the routing engine while agents remain available on any channel. Do
    not deactivate Focus during during normal operating hours to avoid disrupting agents using Focus.
  </>
);

const handleSave = (formList, dispatch) => () => {
  const validForm = formList.every(({ resourceEffects, currentState }) =>
    dispatch(resourceEffects.effects.validate(currentState))
  );

  if (!validForm) {
    return;
  }
  formList.forEach(({ resourceEffects, currentState }) =>
    dispatch(
      resourceEffects.effects.replace(currentState, currentState, {
        customToastMsg: 'Updated conversation workflow settings',
      })
    )
  );
};

const AutoAcceptSettings = isAutoAcceptEnabled => {
  return (
    <>
      <StyledRow>
        <div>
          <StyledBoldSmall>Auto-accept incoming calls</StyledBoldSmall>
          <Checkbox
            attr="autoAccept.enabled"
            data-aid="autoAcceptCallEnabledInput"
            label="Incoming calls will be auto-accepted."
          />
        </div>
      </StyledRow>
      {isAutoAcceptEnabled && (
        <StyledRow>
          <StyledTimeInputField
            suffixText="s"
            allowDecimalDigits={false}
            attr="autoAccept.delaySecs"
            data-aid="autoAcceptDelaySecsInput"
            displayUnits={TimeUnits.Seconds}
            sourceUnits={TimeUnits.Seconds}
            placeholder="10"
            label="Auto-accept call time limit (seconds)"
            description="The amount of time before an incoming call will be automatically accepted"
          />
        </StyledRow>
      )}
    </>
  );
};

const isEqualHelper = (prev, next) => {
  const prevListNormalized = Object.values(prev).map(item => (isNumber(item) ? `${item}` : item));
  const nextListNormalized = Object.values(next).map(item => (isNumber(item) ? `${item}` : item));
  return isEqual(prevListNormalized, nextListNormalized);
};

const RoutingForm = ({ item, errors }: FormEditorProps) => {
  const dispatch = useDispatch();

  useResourceAll(agentsResource);

  const formContext = useForm({
    item,
    errors,
    resetErrors: [
      voiceConfigResource.actions.resetErrors,
      taskRoutingResource.actions.resetErrors,
      conversationWorkflowResource.actions.resetErrors,
      messagingResource.actions.resetErrors,
    ],
  });
  const isFeatureEnabled = useIsFeatureEnabled();
  const { loadingState: agentsLoadingState, values: agentOptionsBase } = useAgents();
  const agentOptions = useMemo(() => [{ label: 'None', value: '' }, ...agentOptionsBase], [agentOptionsBase]);

  const { updateState, workingState } = formContext;
  const fromConfig = useMemo(
    () => [
      {
        prevState: getVoiceData(item),
        currentState: getVoiceData(workingState),
        resourceEffects: voiceConfigResource,
      },
      {
        prevState: getTaskData(item),
        currentState: getTaskData(workingState),
        resourceEffects: taskRoutingResource,
      },
      {
        prevState: getConversationWorkflowData(item),
        currentState: getConversationWorkflowData(workingState),
        resourceEffects: conversationWorkflowResource,
      },
      {
        prevState: getMessageData(item),
        currentState: getMessageData(workingState),
        resourceEffects: messagingResource,
      },
    ],
    [item, workingState]
  );

  const formList = fromConfig.filter(({ prevState, currentState }) => !isEqualHelper(prevState, currentState));
  const { showPreview, autoAssignMessagingSessions, enableFocusMode } = workingState;

  const isCustomAttributesEnabled = isFeatureEnabled('customAttributes');
  const isAutoCloseAbandonedChatsEnabled = isFeatureEnabled('autoCloseAbandonedChats');

  const customAttributesLabel = `Require every Conversation to have a topic ${
    isCustomAttributesEnabled ? '(configurable or freeform)' : ''
  } applied before it can be closed`;

  const isModified = !isEmpty(formList);
  const { renderConfirmation } = useOnLeavePage({ isModified, title: 'Routing' });

  const onFocusModeChange = useCallback(
    value => {
      updateState(set({}, 'enableFocusMode', value));
    },
    [updateState]
  );

  return (
    <>
      <Form context={formContext} preventSaveOnEnter>
        <StyledBody data-aid="routing-form-editor">
          <Section header="Conversation">
            <StyledRow>
              <StyledTimeInputField
                suffixText="min"
                allowDecimalDigits={false}
                attr="reopenConversationWindow"
                data-aid="reopenConversationWindowInput"
                displayUnits={TimeUnits.Minutes}
                sourceUnits={TimeUnits.StringFormat}
                placeholder="15"
                label="Conversation reopen window (minutes)"
                description="The time frame in which a Customer can reopen a previous Conversation after it has been closed before a new one is created."
              />
            </StyledRow>
            <StyledRow>
              <div>
                <StyledBoldSmall>Topic assignment</StyledBoldSmall>
                <Checkbox label={customAttributesLabel} attr="requireTopics" data-aid="requireTopicsInput" />
              </div>
            </StyledRow>
            {agentsLoadingState !== 'loading' && (
              <StyledRow>
                <StyledAgentDropdown
                  attr="sidekickAssignedAgentId"
                  data-aid="sidekickAssignedAgentIdInput"
                  label="Sidekick assigned agent"
                  description="The agent to assign when Sidekick resolves a conversation without human handoff. This is used for CSAT integrations that require an agent ID."
                  options={agentOptions}
                  placeholder="Select an agent"
                  searchable
                  allowEmptyValues
                />
              </StyledRow>
            )}
            <StyledRow>
              <div>
                <StyledBoldSmall>Lookup before Rules</StyledBoldSmall>
                <StyledRowSmall>
                  This ensures Gladly has data from your lookup adapter when applying rules.
                </StyledRowSmall>
                <Checkbox
                  attr="lookupBeforeRouting.enabled"
                  data-aid="lookupBeforeRoutingEnabledInput"
                  label="Allow lookup to run before rules"
                />
              </div>
            </StyledRow>
            <StyledRow>
              <StyledTimeInputField
                suffixText="s"
                allowDecimalDigits={false}
                attr="lookupBeforeRouting.timeoutSecs"
                data-aid="lookupBeforeRoutingTimeoutSecsInput"
                displayUnits={TimeUnits.Seconds}
                sourceUnits={TimeUnits.Seconds}
                placeholder="15"
                label="Customer lookup timeout (seconds)"
                description="The amount of time allocated to lookup a customer before inbound rules run."
              />
            </StyledRow>
            <StyledLine />
          </Section>

          <Section header="Email and Voicemail">
            <StyledRow>
              <StyledTimeInputField
                suffixText="min"
                allowDecimalDigits={false}
                attr="endMailSessionIntervalMins"
                data-aid="endMailSessionIntervalMinsInput"
                displayUnits={TimeUnits.Minutes}
                sourceUnits={TimeUnits.Minutes}
                placeholder="15"
                label="Same agent assignment time limit (minutes)"
                description={SameAgentInputDescriptionEmail}
              />
            </StyledRow>
            <StyledBold>Assign inbound messages to...</StyledBold>
            <StyledRowSmall>
              <StyledRadioGroup
                className="radioGroup"
                attr="assignMailToConversationAssignee"
                data-aid="assign-mail-to-conversation-radio-group"
                options={[
                  {
                    label: 'Same Agent if conversation is open or reopened',
                    value: true,
                    description:
                      'New email and voicemail replies are routed to the same Agent currently assigned to the conversation.',
                  },
                  {
                    label: 'Next available Agent',
                    value: false,
                    description: 'New email and voicemail replies are routed and assigned to the next available Agent.',
                  },
                ]}
              />
            </StyledRowSmall>
            <StyledLine />
          </Section>
          <H3>Voice</H3>
          <Section>
            {AutoAcceptSettings(get(workingState, 'autoAccept.enabled', false))}
            <StyledRow>
              <div>
                <StyledBoldSmall>Decline calls</StyledBoldSmall>
                <ReversedValueCheckbox
                  attr="disableDeclineCall"
                  data-aid="disableDeclineCallInput"
                  label="Allow agents to decline new phone calls"
                />
              </div>
            </StyledRow>
            <StyledRow>
              <StyledTimeInputField
                suffixText="s"
                allowDecimalDigits={false}
                attr="callWrapUpIntervalSecs"
                data-aid="callWrapUpIntervalSecsInput"
                displayUnits={TimeUnits.Seconds}
                sourceUnits={TimeUnits.Seconds}
                placeholder="20"
                label="After call work (seconds)"
                description="At the end of a call, the amount of time until an Agent is routed a new call."
              />
            </StyledRow>
            <StyledRow>
              <StyledTimeInputField
                suffixText="s"
                allowDecimalDigits={false}
                attr="declineCallAutoReadyIntervalSecs"
                data-aid="declineCallAutoReadyIntervalSecsInput"
                displayUnits={TimeUnits.Seconds}
                sourceUnits={TimeUnits.Seconds}
                placeholder="20"
                label="Time before next call after declining (seconds)"
                description="After declining or missing a call, the amount of time until an Agent is routed a new call."
              />
            </StyledRow>
            <StyledLine />
          </Section>
          <H3>Messaging</H3>
          <StyledText>Applies to chat, SMS, and all social channels</StyledText>
          <Section>
            <StyledRow>
              <StyledInputField
                data-aid="agentCapacityInput"
                attr="agentCapacity"
                label="Standard messaging capacity per Agent"
                description="The number of messaging Contacts an Agent can be automatically assigned simultaneously."
                placeholder="3"
                type="number"
              />
            </StyledRow>
            <StyledRow>
              <StyledInputField
                data-aid="agentMaxCapacity"
                attr="agentMaxCapacity"
                label="Maximum messaging capacity per Agent"
                description="The greatest number of messages an Agent can work on simultaneously. If an Agent has the capacity, they can manually request additional messaging Customers up to this limit."
                placeholder="10"
                type="number"
              />
            </StyledRow>
            <StyledRow>
              <StyledTimeInputField
                suffixText="s"
                allowDecimalDigits={false}
                attr="routeNextMessagingSessionIntervalSecs"
                data-aid="routeNextMessagingSessionIntervalSecsInput"
                displayUnits={TimeUnits.Seconds}
                sourceUnits={TimeUnits.Seconds}
                placeholder="20"
                label="Next message routing delay (seconds)"
                description="After accepting, declining, or ending a message, the amount of time before an Agent is routed a new message."
              />
            </StyledRow>
            <StyledRow>
              <StyledTimeInputField
                suffixText="min"
                allowDecimalDigits={false}
                attr="endMessagingSessionIntervalMins"
                data-aid="endMessagingSessionIntervalMinsInput"
                displayUnits={TimeUnits.Minutes}
                sourceUnits={TimeUnits.Minutes}
                placeholder="30"
                label="Same agent assignment time limit (minutes)"
                description={<SameAgentInputDescriptionMessage />}
              />
            </StyledRow>
            <StyledRow>
              <div>
                <StyledBoldSmall>Inbound message assignment</StyledBoldSmall>
                <ReversedValueCheckbox
                  attr="autoAssignMessagingSessions"
                  data-aid="autoAssignMessagingSessionsInput"
                  label="Allow agents to accept or decline new messages"
                />
              </div>
            </StyledRow>
            <StyledRow>
              <div>
                <StyledBoldSmall>Notification banner preview</StyledBoldSmall>
                <Checkbox
                  label="Include a preview of the customer's message in the notification banner"
                  attr="showPreview"
                  data-aid="showPreviewInput"
                />
              </div>
            </StyledRow>
            <StyledRow>
              <StyledBannerBody>
                <StyledBannerTitle>Example notification banner:</StyledBannerTitle>
                <BannerComponent showPreview={showPreview} autoAssignMessagingSessions={autoAssignMessagingSessions} />
              </StyledBannerBody>
            </StyledRow>
            {isAutoCloseAbandonedChatsEnabled && (
              <>
                <StyledRow>
                  <div>
                    <StyledBoldSmall>Abandoned chat contacts</StyledBoldSmall>
                    <Checkbox
                      label="Automatically end chat contacts when customers abandon their mobile web or desktop chat."
                      attr="closeAbandonedChatSessions"
                      data-aid="closeAbandonedChatSessionsInput"
                    />
                  </div>
                </StyledRow>
                <StyledRow>
                  <StyledTimeInputField
                    suffixText="s"
                    allowDecimalDigits={false}
                    attr="chatSessionRejoinWindowSecs"
                    data-aid="chatSessionRejoinWindowSecsInput"
                    displayUnits={TimeUnits.Seconds}
                    sourceUnits={TimeUnits.Seconds}
                    placeholder="60"
                    label="Chat contact rejoin window (seconds)"
                    description="The amount of time during which a customer can rejoin an abandoned chat before it is ended and the Agent's capacity (concurrency slot) is freed up."
                  />
                </StyledRow>
              </>
            )}
            <StyledLine />
          </Section>

          <Section header="Tasks">
            <StyledRow>
              <StyledTimeInputField
                suffixText="min"
                allowDecimalDigits={true}
                attr="routingWindowInSeconds"
                data-aid="routingWindowInSecondsInput"
                displayUnits={TimeUnits.Minutes}
                sourceUnits={TimeUnits.Seconds}
                placeholder="15"
                label="Task routing window (minutes)"
                description="The amount of time prior to a task being due before it gets routed or appears in an Agent’s Customer List"
              />
            </StyledRow>
            <StyledLine />
          </Section>
          <Section header="Focus">
            <StyledRow>
              <Switch
                data-aid={enableFocusMode ? 'focusMode-enabled' : 'focusMode-disabled'}
                attr="enableFocusMode"
                label=""
                onChange={onFocusModeChange}
              />
              <div>{enableFocusMode ? renderFocusOnMessage() : renderFocusOffMessage()}</div>
            </StyledRow>
          </Section>
        </StyledBody>
        <Footer>
          <Button data-aid="routing-form-save" disabled={isEmpty(formList)} onClick={handleSave(formList, dispatch)}>
            Save
          </Button>
        </Footer>
      </Form>
      {renderConfirmation}
    </>
  );
};

const Body = styled(InsetContainer).attrs({ inset: 'large' })`
  flex-grow: 1;
  max-width: 684px;
  width: 100%;
  padding-top: 0;
`;

const StyledInputField = styled(InputField)`
  ${ControlDescription} {
    color: ${p => p.theme.colors.gray800};
    margin-bottom: 0px;
  }

  ${ControlLabel} {
    margin-bottom: 0px;
  }

  ${FieldWrapper} {
    max-width: 100px;
    input {
      width: 100%;
    }
  }
`;

const StyledTimeInputField = styled(TimeInputField)`
  ${ControlDescription} {
    color: ${p => p.theme.colors.gray800};
    margin-bottom: 0px;
  }

  ${FieldWrapper} {
    max-width: 100px;
  }
`;

const StyledAgentDropdown = styled(Dropdown)`
  ${ControlDescription} {
    color: ${p => p.theme.colors.gray800};
    margin-bottom: 0px;
  }

  ${ControlLabel} {
    margin-bottom: 0px;
  }

  ${ControlWrapper} {
    max-width: 50%;
  }
`;

const StyledRow = styled(Row)`
  margin-bottom: 24px;
`;

const StyledRowSmall = styled(Row)`
  margin-bottom: 14px;
`;

const StyledBannerRow = styled(Row)`
  margin-bottom: 0;
`;

const StyledText = styled.div`
  margin-bottom: 20px;
  color: ${p => p.theme.colors.gray700};
`;

const StyledStrong = styled.span`
  font-weight: ${p => p.theme.fontWeight.heavy};
`;

const StyledBold = styled.div`
  font-weight: ${p => p.theme.fontWeight.heavy};
  margin-bottom: ${p => p.theme.spacing.insetMedium};
`;

const StyledBoldSmall = styled.div`
  font-weight: ${p => p.theme.fontWeight.heavy};
  margin-bottom: ${p => p.theme.spacing.insetSmall};
`;

const StyledLine = styled.div`
  border-top: 1px solid ${p => p.theme.colors.gray300};
`;

const StyledBannerBody = styled.div`
  width: 100%;
`;

const StyledBannerTitle = styled.div`
  margin-bottom: 8px;
  font-size: ${p => p.theme.fontSize.base};
  color: ${p => p.theme.colors.gray800};
  font-weight: ${p => p.theme.fontWeight.normal};
  width: 100%;
`;

const StyledBody = styled(Body)`
  max-width: 100%;
  padding-bottom: 0;

  ${StyledRow} {
    max-width: 684px;
  }

  ${StyledBannerRow} {
    max-width: 684px;
  }

  ${StyledSection}:last-child {
    ${StyledRow} {
      margin-bottom: 0;
    }
  }
`;

const FocusStatus = styled.div`
  font-weight: ${p => p.theme.fontWeight.heavy};
  margin-bottom: ${p => p.theme.spacing.stackXSmall};
`;

export default RoutingForm;
